import { FC, useState } from 'react';

import RichText from '@eneco/components/RichText/RichText';
import { useFormatter } from '@eneco/i18n';
import { Bucket, Button, ButtonLink, Heading, Stack, Text, TextLink } from '@eneco/sparky';
import { AdvanceCard as ConsumptionGraphicalOverviewAdvanceCard } from '@sitecore/types/ConsumptionGraphicalOverview';
import { AdvanceCard } from '@sitecore/types/ManageAdvanceAmount';

import AlignYBaseline from '../../../components/AlignYBaseLine';
import BorderedBox from '../../../components/BorderedBox';
import DataErrorNotification from '../../../components/DataErrorNotification';
import InfoComponent from '../../../components/InfoComponent';
import ReconcileAdvanceAmountDisclaimer from '../../../components/ReconcileAdvanceAmountDisclaimer';
import { addressSearchParam } from '../../../utils/addSearchParams';

interface Fields {
  advanceCard: AdvanceCard & Partial<ConsumptionGraphicalOverviewAdvanceCard>;
}

interface AdvanceCardProps {
  fields: Fields;
  recommendedAmount: number | null | undefined;
  currentAmount: number | null | undefined;
  reconcileAdvanceAmount: number | undefined | null;
  hideRecommendedAmount: boolean;
  hideEdit: boolean;
  addressIdentifier: string;
  globalRecommendedAdvancesOnChange: (amount: number | null | undefined) => Promise<void>;
  isEnergyMonitorView?: boolean;
  hasAnalogueMeter?: boolean;
}

const TotalAdvancesCard: FC<AdvanceCardProps> = ({
  fields,
  recommendedAmount,
  currentAmount,
  reconcileAdvanceAmount,
  hideRecommendedAmount,
  hideEdit,
  addressIdentifier,
  globalRecommendedAdvancesOnChange,
  isEnergyMonitorView,
  hasAnalogueMeter,
}) => {
  const { currency } = useFormatter();
  const [isLoadingSubmit, setIsLoadingSubmit] = useState<boolean>(false);
  const [error, setError] = useState<boolean>(false);
  return (
    <Bucket title={fields.advanceCard?.title?.value}>
      <Bucket.Content>
        {error && <DataErrorNotification />}
        {currentAmount != null && (
          <Stack gap="4">
            <Text weight="bold">{fields.advanceCard?.currentAmountTitle?.value}</Text>
            <AlignYBaseline>
              <Heading as="h3" size="L">
                {Math.round(currentAmount) === currentAmount
                  ? currency.euroNoFractionDigits(currentAmount)
                  : currency.euro(currentAmount)}
              </Heading>
              <Text weight="bold">{fields.advanceCard?.eachMonthLabel?.value}</Text>
            </AlignYBaseline>
          </Stack>
        )}
        {!hideRecommendedAmount &&
          recommendedAmount != null &&
          recommendedAmount !== 0 &&
          recommendedAmount !== currentAmount && (
            <BorderedBox>
              <Stack gap="6">
                <Stack direction="row" alignX="justify" alignY="center" gap="4" wrap>
                  <Stack gap="4">
                    <Text weight="bold">{fields.advanceCard?.recommendedAmountTitle?.value}</Text>
                    <AlignYBaseline wrap>
                      <Heading as="h3" size="M">
                        {Math.round(recommendedAmount) === recommendedAmount
                          ? currency.euroNoFractionDigits(recommendedAmount)
                          : currency.euro(recommendedAmount)}
                      </Heading>
                      <Text weight="bold">{fields.advanceCard?.eachMonthLabel?.value}</Text>
                    </AlignYBaseline>
                  </Stack>
                  <Stack>
                    {!hideEdit &&
                      (isEnergyMonitorView ? (
                        <ButtonLink
                          href={
                            fields.advanceCard?.acceptRecommendedAmountLink?.value?.href +
                            addressSearchParam(addressIdentifier)
                          }
                          action="primary"
                          size="compact">
                          {fields.advanceCard?.acceptRecommendedAmountLink?.value?.text}
                        </ButtonLink>
                      ) : (
                        <Button
                          action="primary"
                          size="compact"
                          type="button"
                          isLoading={isLoadingSubmit}
                          onClick={async () => {
                            setIsLoadingSubmit(true);
                            try {
                              await globalRecommendedAdvancesOnChange(recommendedAmount);
                            } catch (e) {
                              void e;
                              setError(true);
                            }
                            setIsLoadingSubmit(false);
                          }}>
                          {fields.advanceCard?.acceptRecommendedAmountLink?.value?.text}
                        </Button>
                      ))}
                  </Stack>
                </Stack>
                {isEnergyMonitorView ? (
                  <Text size="BodyM">
                    {fields.advanceCard?.energyMonitorDisclaimerInformationText?.value ||
                      'Deze schatting is [not in Sitecore]'}
                    <InfoComponent
                      variant="icon"
                      triggerText={
                        fields.advanceCard?.energyMonitorDisclaimerInformationText?.value ||
                        'Deze schatting is [not in Sitecore]'
                      }>
                      {fields.advanceCard?.energyMonitorDisclaimerInfoPopoverText?.value ||
                        'Deze schatting is [not in Sitecore]'}
                    </InfoComponent>
                  </Text>
                ) : (
                  <ReconcileAdvanceAmountDisclaimer
                    amount={reconcileAdvanceAmount}
                    recommendedAmountTooLowDisclaimerLabel={
                      fields.advanceCard?.recommendedAmountTooLowDisclaimerLabel?.value
                    }
                    recommendedAmountTooHighDisclaimerLabel={
                      fields.advanceCard?.recommendedAmountTooHighDisclaimerLabel?.value
                    }
                  />
                )}
              </Stack>
            </BorderedBox>
          )}
        {isEnergyMonitorView && (
          <Text size="BodyM">
            <RichText
              html={
                hasAnalogueMeter
                  ? fields.advanceCard?.energyMonitorAnalogueDescription?.value
                  : fields.advanceCard?.energyMonitorDigitalDescription?.value
              }
            />
          </Text>
        )}
        <Stack.Item>
          {!hideEdit && (
            <TextLink
              href={fields.advanceCard?.editAdvanceAmountLink?.value?.href + addressSearchParam(addressIdentifier)}
              emphasis="high">
              {fields.advanceCard?.editAdvanceAmountLink?.value?.text}
            </TextLink>
          )}
        </Stack.Item>
      </Bucket.Content>
    </Bucket>
  );
};

export default TotalAdvancesCard;
